import type { DatePickerType } from '@/src/components/DatePicker/datePicker.types'
import { PlusMinusToggleEnum } from '@/src/components/Form/FormPlusMinusToggle'
import { GranularityEnum } from '@/src/enums/granularity.enum'
import { AdjustmentEnergyTypeDto, LoadAdjustmentTypeDto } from '@api/types/generated-types'
import type {
  AbsoluteQuantityAdjustmentResponseDTO,
  CompanyHolidaysResponseDTO,
  FlexibilityAdjustmentResponseDTO,
  MeasureSimulationCandidateDTO,
  PercentageQuantityAdjustmentResponseDTO,
  RenewableProductionAdjustmentResponseDTO,
} from '@api/types/procurement'
import { appColors } from '@theme/foundations/colors'
import type { TFunction } from 'react-i18next'

export type AllAdjustmentTypes =
  | CompanyHolidaysResponseDTO
  | AbsoluteQuantityAdjustmentResponseDTO
  | PercentageQuantityAdjustmentResponseDTO
  | FlexibilityAdjustmentResponseDTO
  | RenewableProductionAdjustmentResponseDTO
export type CreationBasedAdjustmentsWithType = (
  | CompanyHolidaysResponseDTO
  | AbsoluteQuantityAdjustmentResponseDTO
  | PercentageQuantityAdjustmentResponseDTO
) & { type: AdjustmentTypeEnum }
export type AllAdjustmentsWithMeasureCandidates = AllAdjustmentTypes | MeasureSimulationCandidateDTO
export type NonMeasureAdjustmentsWithType = AllAdjustmentTypes & { type: AdjustmentTypeEnum }
export type QuantityAdjustmentsWithType = (AbsoluteQuantityAdjustmentResponseDTO | PercentageQuantityAdjustmentResponseDTO) & {
  type: AdjustmentTypeEnum
}

export enum AdjustmentCreationType {
  CREATION_BASED = 'CREATION_BASED',
  MEASURE_BASED = 'MEASURE_BASED',
}

export interface LoadPlanningRequestProps {
  companyId?: number
  locationId?: number
  energyType: 'ELECTRICITY' | 'GAS' | 'UNKNOWN'
  year?: number
  isMeasureSimulationEnabled: boolean
}

export enum MeasureSimulationTypeDto {
  ONSITE_GENERATION = 'ONSITE_GENERATION',
  EFFICIENCY_IMPROVEMENT = 'EFFICIENCY_IMPROVEMENT',
  FLEXIBILITY = 'FLEXIBILITY',
}

export enum AdjustmentTypeEnum {
  COMPANY_HOLIDAYS = 'COMPANY_HOLIDAYS',
  FLEXIBILITY = 'FLEXIBILITY',
  RENEWABLE_PRODUCTION = 'RENEWABLE_PRODUCTION',
  ABSOLUTE_QUANTITY = 'QUANTITY_ADJUSTMENT',
  PERCENTAGE_QUANTITY = 'PERCENTAGE_QUANTITY',
  MEASURE_SIMULATION_CANDIDATE = 'MEASURE_SIMULATION_CANDIDATE',
}

export const ADJUSTMENT_TYPE_LABEL_MAP = (t: TFunction<'translation', undefined>) => ({
  [LoadAdjustmentTypeDto.COMPANY_HOLIDAYS]: t('components.procurement.forecasting.select_labels.adjustment_type.company_holidays'),
  [LoadAdjustmentTypeDto.QUANTITY_ADJUSTMENT]: t('components.procurement.forecasting.select_labels.adjustment_type.quantity_adjustment'),
})

export const adjustmentTypeTranslation = (adjustmentType: AdjustmentTypeEnum, hasMeasureIntegration: boolean) => {
  switch (adjustmentType) {
    case AdjustmentTypeEnum.COMPANY_HOLIDAYS:
      return 'components.procurement.forecasting.select_labels.adjustment_type.company_holidays'
    case AdjustmentTypeEnum.RENEWABLE_PRODUCTION:
      return 'measure_simulation.side_panel.form.measure_types.generation'
    case AdjustmentTypeEnum.FLEXIBILITY:
      return 'measure_simulation.side_panel.form.measure_types.flexibility'
    case AdjustmentTypeEnum.PERCENTAGE_QUANTITY:
    case AdjustmentTypeEnum.ABSOLUTE_QUANTITY:
      return hasMeasureIntegration
        ? 'measure_simulation.side_panel.form.measure_types.efficiency'
        : 'components.procurement.forecasting.select_labels.adjustment_type.quantity_adjustment'
    default:
      return 'components.procurement.forecasting.select_labels.adjustment_type.quantity_adjustment'
  }
}

export const ADJUSTMENT_GRANULARITY_LABEL_MAP = (t: TFunction<'translation', undefined>) => ({
  [LoadAdjustmentGranularityDto.WEEKLY]: t('components.procurement.forecasting.select_labels.granularity.weekly'),
  [LoadAdjustmentGranularityDto.MONTHLY]: t('components.procurement.forecasting.select_labels.granularity.monthly'),
})

export const MEASURE_SIMULATION_TYPE_LABEL_MAP = (t: TFunction<'translation', undefined>) => ({
  [MeasureSimulationTypeDto.ONSITE_GENERATION]: t('measure_simulation.side_panel.form.measure_types.generation'),
  [MeasureSimulationTypeDto.EFFICIENCY_IMPROVEMENT]: t('measure_simulation.side_panel.form.measure_types.efficiency'),
  [MeasureSimulationTypeDto.FLEXIBILITY]: t('measure_simulation.side_panel.form.measure_types.flexibility'),
})

export const mapToEnergyTypeDto = (energyType?: 'ELECTRICITY' | 'GAS' | 'UNKNOWN'): AdjustmentEnergyTypeDto => {
  switch (energyType) {
    case 'ELECTRICITY':
      return AdjustmentEnergyTypeDto.ELECTRICITY
    case 'GAS':
      return AdjustmentEnergyTypeDto.GAS
    default:
      return AdjustmentEnergyTypeDto.ELECTRICITY
  }
}

export const ADJUSTMENT_VALUE_UNIT_LABEL_MAP = () => ({
  [LoadAdjustmentValueUnitDto.KWH]: 'kWh',
  [LoadAdjustmentValueUnitDto.KW]: 'kW',
})

export type AdjustmentTypeName =
  | 'companyHolidays'
  | 'absoluteQuantity'
  | 'percentageQuantity'
  | 'flexibility'
  | 'renewableProduction'
  | 'measureSimulationCandidates'

export const mapAdjustmentStringToEnum = (adjustmentType: AdjustmentTypeName) => {
  switch (adjustmentType) {
    case 'companyHolidays':
      return AdjustmentTypeEnum.COMPANY_HOLIDAYS
    case 'absoluteQuantity':
      return AdjustmentTypeEnum.ABSOLUTE_QUANTITY
    case 'percentageQuantity':
      return AdjustmentTypeEnum.PERCENTAGE_QUANTITY
    case 'flexibility':
      return AdjustmentTypeEnum.FLEXIBILITY
    case 'renewableProduction':
      return AdjustmentTypeEnum.RENEWABLE_PRODUCTION
    case 'measureSimulationCandidates':
      return AdjustmentTypeEnum.MEASURE_SIMULATION_CANDIDATE
  }
}

export const getAdjustmentUnit = (selectedAdjustment: NonMeasureAdjustmentsWithType) => {
  if (selectedAdjustment.type === AdjustmentTypeEnum.COMPANY_HOLIDAYS) {
    return (selectedAdjustment as CompanyHolidaysResponseDTO).baseLoad.unit
  } else if (selectedAdjustment.type === AdjustmentTypeEnum.RENEWABLE_PRODUCTION) {
    return (selectedAdjustment as RenewableProductionAdjustmentResponseDTO).totalAnnualProduction.unit
  } else if ([AdjustmentTypeEnum.ABSOLUTE_QUANTITY, AdjustmentTypeEnum.FLEXIBILITY].includes(selectedAdjustment.type)) {
    return (selectedAdjustment as AbsoluteQuantityAdjustmentResponseDTO | FlexibilityAdjustmentResponseDTO).adjustmentValue.unit
  } else {
    return 'kWh'
  }
}

export const loadAdjustmentValueUnitSelectOptions = (labelMap: Record<string, string>) =>
  Object.values(LoadAdjustmentValueUnitDto).map((item: string) => ({ label: labelMap[item], value: item }))

export const mapToIncrementDecrement = (e: PlusMinusToggleEnum) => {
  switch (e) {
    case PlusMinusToggleEnum.PLUS:
      return AdjustmentValueChangeTypeDto.INCREMENT
    case PlusMinusToggleEnum.MINUS:
      return AdjustmentValueChangeTypeDto.DECREMENT
  }
}

export const mapToPlusMinusEnum = (e: 'INCREMENT' | 'DECREMENT') => {
  switch (e) {
    case AdjustmentValueChangeTypeDto.INCREMENT:
      return PlusMinusToggleEnum.PLUS
    case AdjustmentValueChangeTypeDto.DECREMENT:
      return PlusMinusToggleEnum.MINUS
    default:
      return PlusMinusToggleEnum.PLUS
  }
}

export const DATE_PICKER_TYPE_MAP: Partial<Record<GranularityEnum, DatePickerType>> = {
  [GranularityEnum.FIFTEEN_MINUTES]: 'datetime',
  [GranularityEnum.ONE_HOUR]: 'datetime',
  [GranularityEnum.ONE_DAY]: 'day',
  [GranularityEnum.ONE_WEEK]: 'week',
  [GranularityEnum.ONE_MONTH]: 'month',
}
export enum LoadAdjustmentGranularityDto {
  MONTHLY = 'MONTHLY',
  WEEKLY = 'WEEKLY',
}

export enum LoadAdjustmentChangeUnitDto {
  PERCENTAGE = 'PERCENTAGE',
  ABSOLUTE = 'ABSOLUTE',
}

export const DATE_PICKER_FROM_ENUM_MAP: Partial<Record<LoadAdjustmentGranularityDto, DatePickerType>> = {
  [LoadAdjustmentGranularityDto.WEEKLY]: 'week',
  [LoadAdjustmentGranularityDto.MONTHLY]: 'month',
}

export const CHANGE_UNIT_TRANSLATION_STRING_FROM_ENUM_MAP: Partial<Record<LoadAdjustmentChangeUnitDto, string>> = {
  [LoadAdjustmentChangeUnitDto.ABSOLUTE]: 'components.procurement.forecasting.modal.form.change_unit_absolute',
  [LoadAdjustmentChangeUnitDto.PERCENTAGE]: 'components.procurement.forecasting.modal.form.change_unit_percentage',
}

export enum LoadAdjustmentValueUnitDto {
  KWH = 'KWH',
  KW = 'KW',
}

export enum AdjustmentValueChangeTypeDto {
  INCREMENT = 'INCREMENT',
  DECREMENT = 'DECREMENT',
}

export enum LoadAdjustmentCreationTypeDto {
  COMPANY_HOLIDAYS = 'COMPANY_HOLIDAYS',
  QUANTITY_ADJUSTMENT = 'QUANTITY_ADJUSTMENT',
}

type ProcurementStatus =
  | 'active'
  | 'procured'
  | 'pending'
  | 'open'
  | 'bought'
  | 'fixed'
  | 'draft'
  | 'saved'
  | 'submitted'
  | 'DRAFT'
  | 'SAVED'
  | 'SUBMITTED'
  | 'FIXED'

export const getStatusTagColor = (status: ProcurementStatus) => {
  switch (status) {
    case 'draft':
    case 'DRAFT':
      return appColors.secondary[500]
    case 'saved':
    case 'SAVED':
    case 'submitted':
    case 'SUBMITTED':
      return appColors.info
    case 'fixed':
    case 'FIXED':
      return '#10B981'
    default:
      return ''
  }
}

export const getStatusTagColorScheme = (status: ProcurementStatus) => {
  switch (status) {
    case 'procured':
    case 'fixed':
    case 'bought':
    case 'FIXED':
      return { background: appColors.green['100'], textColor: appColors.positive, hover: '' }
    case 'pending':
    case 'saved':
    case 'SAVED':
    case 'submitted':
    case 'SUBMITTED':
      return { background: appColors.blue['100'], textColor: appColors.info, hover: '' }
    case 'open':
    case 'draft':
    case 'DRAFT':
      return { background: appColors.amber['50'], textColor: appColors.warning, hover: '' }
    default:
      return { background: appColors.amber['50'], textColor: appColors.warning, hover: '' }
  }
}
